/**
 * localStorage-backed quiz stats persistence.
 *
 * Schema (per quiz_type):
 *   - todayDate: YYYY-MM-DD when the today-counters were last updated
 *   - todayAttempts / todayCorrect: counters for that date
 *   - currentStreak: persists across reloads + days; only resets on a miss
 *   - bestStreak: high-water mark across all time
 *
 * Day rollover: when today's date changes, today-counters reset but
 * streak + best stay. So a player who finished yesterday at 5/5 with
 * a 5-streak opens the app today and sees "Today: 0/0" but "Streak: 5".
 *
 * If localStorage is unavailable (private browsing, full quota), all
 * functions degrade silently to the session-only behavior.
 */

import { INITIAL_SESSION_STATS, type SessionStats } from "@/features/quiz/lib/common";

const SCHEMA_VERSION = 1;

interface StoredStats {
  v: number;
  todayDate: string;
  todayAttempts: number;
  todayCorrect: number;
  currentStreak: number;
  bestStreak: number;
}

/** Local-time YYYY-MM-DD string for "today." Uses the user's wall clock
 *  rather than UTC so the day boundary feels right wherever they are. */
export function todayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function storageKey(quizType: string): string {
  return `pt:quiz:${quizType}`;
}

/** Load stats for a quiz. Returns INITIAL_SESSION_STATS if no record
 *  exists OR localStorage is unavailable. Handles day rollover. */
export function loadStats(quizType: string, now: Date = new Date()): SessionStats {
  try {
    const raw = localStorage.getItem(storageKey(quizType));
    if (!raw) return INITIAL_SESSION_STATS;
    const parsed = JSON.parse(raw) as StoredStats;
    if (parsed.v !== SCHEMA_VERSION) return INITIAL_SESSION_STATS;
    const isToday = parsed.todayDate === todayKey(now);
    return {
      attempts: isToday ? parsed.todayAttempts : 0,
      correct: isToday ? parsed.todayCorrect : 0,
      streak: parsed.currentStreak,
      bestStreak: parsed.bestStreak,
    };
  } catch {
    return INITIAL_SESSION_STATS;
  }
}

/** Write stats. Silently no-ops on storage failure. */
export function saveStats(
  quizType: string,
  stats: SessionStats,
  now: Date = new Date(),
): void {
  try {
    const data: StoredStats = {
      v: SCHEMA_VERSION,
      todayDate: todayKey(now),
      todayAttempts: stats.attempts,
      todayCorrect: stats.correct,
      currentStreak: stats.streak,
      bestStreak: stats.bestStreak,
    };
    localStorage.setItem(storageKey(quizType), JSON.stringify(data));
  } catch {
    // Storage unavailable — stats stay session-only. Same behavior as
    // pre-persistence, so no visible regression.
  }
}
